import React, { useState } from 'react';
import { FaFileInvoiceDollar, FaFileAlt } from 'react-icons/fa';
import YearSelector from '../../components/common/YearSelector';
import TabNavigation from '../../components/common/TabNavigation';
import DeclaracionesJuradasTab from '../../components/DeclaracionRenta/DeclaracionesJuradasTab';
import DeclararRentaTab from '../../components/DeclaracionRenta/DeclararRentaTab';
import useYearSelector from '../../hooks/useYearSelector';
import useDeclaracionRenta from '../../hooks/useDeclaracionRenta';
import '../../styles/pages/ServiciosSII/DeclaracionRentaPage.css';

/**
 * Página principal de Declaración de Renta
 * Arquitectura modular diseñada para ser extendida por agentes autónomos vía MCP
 *
 * Esta página integra:
 * - Hook useYearSelector: Para manejo del selector de año
 * - Hook useDeclaracionRenta: Para lógica de negocio
 * - Componentes modulares: YearSelector, TabNavigation, tabs específicos
 * - Servicio declaracionRentaService: Para llamadas API
 *
 * @param {Object} props
 * @param {string} props.token - Token de autenticación
 * @param {number} props.id_company - ID de la empresa
 * @param {string} props.rut_empresa - RUT de la empresa
 */
const DeclaracionRentaPage = ({ token, id_company, rut_empresa }) => {
    // Hook para manejar selección de año
    const {
        selectedYear,
        setSelectedYear,
        yearOptions
    } = useYearSelector(null, 5, 1); // 5 años atrás, 1 año adelante

    // Tab activo (estado local)
    const [activeTab, setActiveTab] = useState('declaraciones-juradas');

    // Hook para manejar datos de declaraciones
    const {
        declaracionesJuradas,
        declaracionesRenta,
        loading,
        error,
        createDeclaracionJurada,
        createDeclaracionRenta,
        updateDeclaracionJurada,
        deleteDeclaracionJurada
    } = useDeclaracionRenta(selectedYear, id_company, token);

    /**
     * Configuración de tabs
     * Puede ser modificada externamente por un agente
     */
    const tabs = [
        {
            id: 'declaraciones-juradas',
            label: 'Declaraciones Juradas',
            icon: <FaFileAlt />
        },
        {
            id: 'declarar-renta',
            label: 'Declarar Renta',
            icon: <FaFileInvoiceDollar />
        }
    ];

    // Manejador de cambio de año
    const handleYearChange = (year) => {
        setSelectedYear(year);
    };

    // Manejador de cambio de tab
    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
    };

    return (
        <div className="declaracion-renta-page">
            {/* Header de la página */}
            <div className="page-header">
                <div className="header-content">
                    <div className="title-section">
                        <FaFileInvoiceDollar className="page-icon" />
                        <div>
                            <h1>Declaración de Renta</h1>
                            <p className="page-subtitle">
                                Gestión de declaraciones de renta y declaraciones juradas
                            </p>
                        </div>
                    </div>

                    {/* Selector de año */}
                    <YearSelector
                        selectedYear={selectedYear}
                        onYearChange={handleYearChange}
                        yearOptions={yearOptions}
                        label="Año Tributario"
                        className="header-year-selector"
                    />
                </div>
            </div>

            {/* Navegación por tabs */}
            <TabNavigation
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                className="page-tabs"
            />

            {/* Contenido de los tabs */}
            <div className="tab-content">
                {activeTab === 'declaraciones-juradas' && (
                    <DeclaracionesJuradasTab
                        declaraciones={declaracionesJuradas}
                        selectedYear={selectedYear}
                        loading={loading}
                        onCreate={createDeclaracionJurada}
                        onUpdate={updateDeclaracionJurada}
                        onDelete={deleteDeclaracionJurada}
                        token={token}
                        idCompany={id_company}
                        rutEmpresa={rut_empresa}
                    />
                )}

                {activeTab === 'declarar-renta' && (
                    <DeclararRentaTab
                        declaraciones={declaracionesRenta}
                        selectedYear={selectedYear}
                        loading={loading}
                        onCreate={createDeclaracionRenta}
                        onUpdate={updateDeclaracionJurada} // Reutilizamos la misma función
                        onDelete={deleteDeclaracionJurada} // Reutilizamos la misma función
                    />
                )}
            </div>

            {/* Footer con información */}
            <div className="page-footer">
                <p className="footer-info">
                    <strong>Nota:</strong> Las declaraciones de renta deben ser presentadas ante el SII
                    durante el mes de abril de cada año. Asegúrese de contar con toda la documentación
                    respaldatoria antes de enviar.
                </p>
            </div>
        </div>
    );
};

export default DeclaracionRentaPage;
